import { Controller } from '@hotwired/stimulus'
import Sortable from 'sortablejs'

// The proposal builder (Events v2). Rows drag within and between the sections (Included, each "choose one" group,
// Add-ons), and each row's hidden choice_group and optional follow the section it lands in, so the section a row sits
// in is what saves. A group's name is typed in its head and copied to its rows. Each section's Add puts a custom line
// or a package in that section; the package chips show only while no row is on the page. Positions are numbered in
// page order before a save or a preview. The guest's page (beside the form from xl, else in the Preview modal)
// redraws from the unsaved values after a pause in typing. A pick inside it (a group's option, an add-on) redraws it at
// once with the picks, until the next edit here. The page's own script never runs in the frame, so the builder
// listens to it. Each redraw keeps the frame where it was scrolled.
// An add-on can go "Only with" some options of the choose-one groups: its chips list the options on the page, by each
// row's ref (new rows mint one here), and are redrawn whenever the page changes. The row keeps its links in
// data-only-with, so one dragged out of Add-ons and back keeps them; only an add-on posts them.
const PREVIEW_DELAY = 700
// The preview page's pick fields: add-ons as picks[], each "choose one" group as choice[n].
const PICK_FIELD = /^(?:picks\[\]|choice\[\d+\])$/

export default class extends Controller {
  static targets = ['form', 'sections', 'section', 'list', 'row', 'rowTemplate', 'groupTemplate', 'frame', 'starter']
  static values = { previewUrl: String }

  // Target callbacks run before connect(), so the instance's fields start here.
  initialize() {
    this.sortables = new Map()
    this.counter = 0
    this.picks = []
    this.scrolls = new WeakMap()
    this.picked = (event) => this.pickInFrame(event)
    this.read = (event) => this.readInFrame(event)
  }

  connect() {
    this.checkGroupNames()
    this.refresh()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.request?.abort()
  }

  listTargetConnected(list) {
    this.sortables.set(list, Sortable.create(list, {
      group: 'pb-rows',
      handle: '.handle',
      draggable: '.pb-row',
      animation: 150,
      onEnd: () => {
        this.restamp()
        this.refresh()
      }
    }))
  }

  listTargetDisconnected(list) {
    this.sortables.get(list)?.destroy()
    this.sortables.delete(list)
  }

  // An Add tile or a chip: a new group (named, with the form's options, or blank with one empty option), rows for the
  // section its kind names (a form field, a chip), or, with no kind, rows for the section whose Add it is (a package,
  // or one blank custom line).
  addPreset(event) {
    const { kind, rows = [] } = event.params
    const values = rows.length ? rows : [{}]
    let section
    if (kind === 'choice') {
      section = this.addGroup(event.currentTarget.getAttribute('data-party-proposal-builder-group-param') || '')
    } else if (kind) {
      section = this.sectionTargets.find((target) => target.dataset.kind === kind)
    } else {
      section = event.currentTarget.closest('.pb-section')
    }
    const added = values.map((value) => this.newRow(value))
    this.listIn(section).append(...added)
    this.restamp()
    this.checkGroupNames()
    this.refresh()

    const groupName = kind === 'choice' && this.groupNameInput(section)
    const focus = groupName && !groupName.value ? groupName : this.nameInput(added[0])
    section.scrollIntoView({ block: 'nearest' })
    focus.focus()
  }

  // A saved row stays in the form, hidden and marked for removal, so the save deletes it; a new one just goes.
  removeRow(event) {
    this.discard(event.currentTarget.closest('.pb-row'))
    this.restamp()
    this.refresh()
  }

  removeGroup(event) {
    const section = event.currentTarget.closest('.pb-section')
    const rows = this.rowsIn(section).filter((row) => !row.hidden)
    const name = this.groupNameInput(section).value.trim() || 'this group'
    if (rows.length && !window.confirm(`Remove ${name} and its ${rows.length === 1 ? 'option' : `${rows.length} options`}?`)) return

    // Removed saved rows move to Included first, so their removal still posts once the group is gone.
    const included = this.listIn(this.sectionTargets.find((target) => target.dataset.kind === 'included'))
    this.rowsIn(section).forEach((row) => {
      this.discard(row)
      if (row.isConnected) included.append(row)
    })
    section.remove()
    this.restamp()
    this.checkGroupNames()
    this.refresh()
  }

  renameGroup() {
    this.restamp()
    this.checkGroupNames()
  }

  // A chip ticked or unticked: the row's links follow.
  link(event) {
    const box = event.target
    const row = box.closest('.pb-row')
    const links = this.linksOf(row).filter((ref) => ref !== box.value)
    row.dataset.onlyWith = JSON.stringify(box.checked ? [...links, box.value] : links)
  }

  // A group has one default: ticking one clears the others.
  pick(event) {
    const box = event.target
    const section = box.closest('.pb-section')
    if (!box.checked || section.dataset.kind !== 'choice') return

    this.rowsIn(section).forEach((row) => {
      const other = this.pickInput(row)
      if (other !== box) other.checked = false
    })
  }

  submit(event) {
    if (event.target === this.formTarget) this.number()
  }

  // An edit here: the redraw shows the defaults the builder sets, as the guest first sees them.
  // A frame still drawing the old rows takes no picks until the redraw loads.
  refresh() {
    this.renderLinks()
    this.picks = []
    this.frameTargets.forEach((frame) => frame.contentDocument?.removeEventListener('change', this.picked))
    clearTimeout(this.timer)
    if (!this.frameTargets.some((frame) => frame.offsetParent)) return

    this.timer = setTimeout(() => this.preview(), PREVIEW_DELAY)
  }

  // The deposit pills write a hidden field without an input event; their clicks count as a change.
  refreshOnRule(event) {
    if (event.target.closest('[data-rule-key]')) this.refresh()
  }

  previewNow() {
    clearTimeout(this.timer)
    this.preview()
  }

  async preview() {
    if (!this.hasPreviewUrlValue || !this.hasFormTarget) return

    this.number()
    this.request?.abort()
    this.request = new AbortController()
    const body = new FormData(this.formTarget)
    body.delete('_method')
    this.picks.forEach(([name, value]) => body.append(name, value))
    try {
      const response = await fetch(this.previewUrlValue, {
        method: 'POST',
        body,
        signal: this.request.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
      })
      if (!response.ok) return

      const html = await response.text()
      this.frameTargets.forEach((frame) => {
        this.scrolls.set(frame, frame.contentWindow?.scrollY || 0)
        frame.srcdoc = html
      })
    } catch (error) {
      if (error.name !== 'AbortError') throw error
    }
  }

  // Private

  // A frame's load (data-action): each srcdoc write loads a new document, so its listener goes on again every time.
  listen({ target: frame }) {
    frame.contentWindow?.scrollTo(0, this.scrolls.get(frame) || 0)
    frame.contentDocument?.addEventListener('change', this.picked)
    // A scroll doesn't bubble, so the document hears the sign dialog's body scroll only while capturing.
    frame.contentDocument?.addEventListener('scroll', this.read, true)
  }

  // The sign dialog, read to its end, fades no more and its locked button reads as party-sign unlocks it on the
  // guest's page. It stays disabled: staff can't sign. Each redraw draws it locked again.
  readInFrame({ target: body }) {
    if (!body.classList?.contains('pe-sign-body')) return

    const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 2
    body.classList.toggle('pe-sign-body--more', !atEnd)
    const button = body.closest('.pe-sign')?.querySelector('[data-unlocked-label]')
    if (!atEnd || !button) return

    button.textContent = button.dataset.unlockedLabel
    button.removeAttribute('aria-disabled')
    delete button.dataset.unlockedLabel
  }

  // Every pick on the page goes with the redraw, the add-ons' leading "" included, so none ticked reads as a pick too.
  // The page's other fields (Accept's name, a question) aren't picks.
  pickInFrame(event) {
    const form = event.target.closest('form.pe-picks')
    if (!form || !PICK_FIELD.test(event.target.name)) return

    this.picks = [...new FormData(form)].filter(([name]) => PICK_FIELD.test(name))
    this.previewNow()
  }

  newRow(values = {}) {
    const index = `${Date.now()}${this.counter++}`
    const template = document.createElement('template')
    template.innerHTML = this.rowTemplateTarget.innerHTML.replace(/NEW_RECORD/g, index)
    const row = template.content.firstElementChild
    const fields = { name: values.name, price: values.price, rich_description: values.rich_description,
                     party_package_id: values.party_package_id }
    Object.entries(fields).forEach(([key, value]) => {
      const input = row.querySelector(`[name$="[${key}]"]`)
      if (input && value !== undefined && value !== null) input.value = value
    })
    if (values.per_guest !== undefined) row.querySelector('[name$="[per_guest]"]').value = String(values.per_guest)
    const ref = this.newRef()
    row.dataset.ref = ref
    this.field(row, 'ref').value = ref
    if (values.party_package_id) row.dataset.packageId = values.party_package_id
    // A package's add-on goes with the rows on the page that came from its packages.
    const packages = (values.only_with_packages || []).map(String)
    const links = this.choiceRows().filter((choice) => !choice.hidden && packages.includes(choice.dataset.packageId))
    row.dataset.onlyWith = JSON.stringify(links.map((choice) => choice.dataset.ref).filter(Boolean))
    return row
  }

  // 8 lowercase base-36 characters, as the server mints them.
  newRef() {
    return Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) => (byte % 36).toString(36)).join('')
  }

  // Each add-on's chips: the shown options of every choose-one group, ticked by its links, then any link whose option
  // is gone, ticked and marked removed, so the save's error has a chip to point at. Rows elsewhere draw none, so post
  // none. A row's chips are redrawn only when the options change, so a ticked chip keeps the focus.
  renderLinks() {
    const groups = new Set()
    // A row saved without a ref (old code mid-deploy) has no key to link by, so it is not offered.
    const options = this.choiceRows().filter((row) => !row.hidden && row.dataset.ref).map((row) => {
      const group = row.closest('.pb-section') && this.groupNameInput(row.closest('.pb-section')).value.trim()
      groups.add(group)
      return { ref: row.dataset.ref, group, name: this.nameInput(row).value.trim() || 'Untitled option' }
    })
    this.rowTargets.forEach((row) => {
      const box = row.querySelector('.pb-row__links')
      if (!box) return

      const addOn = !row.hidden && row.closest('.pb-section')?.dataset.kind === 'add_on'
      const links = this.linksOf(row)
      const chips = !addOn ? [] : [
        ...options.map((option) => ({
          ref: option.ref,
          label: groups.size > 1 && option.group ? `${option.group}: ${option.name}` : option.name,
          checked: links.includes(option.ref)
        })),
        ...links.filter((ref) => !options.some((option) => option.ref === ref))
                .map((ref) => ({ ref, label: `${this.nameOfRef(ref)} (removed)`, checked: true }))
      ]
      box.hidden = chips.length === 0
      const key = JSON.stringify(chips.map((chip) => [chip.ref, chip.label]))
      if (box.dataset.key === key) return

      box.dataset.key = key
      const name = this.field(row, 'only_with').name
      box.querySelector('.pb-row__chips').replaceChildren(...chips.map((chip) => this.linkChip(name, chip)))
    })
  }

  linkChip(name, { ref, label, checked }) {
    const chip = document.createElement('label')
    chip.className = 'ef-chip'
    const input = document.createElement('input')
    Object.assign(input, { type: 'checkbox', className: 'ef-chip__input', name, value: ref, checked })
    input.dataset.action = 'party-proposal-builder#link'
    const icon = document.createElement('i')
    icon.className = 'bi bi-check-lg ef-chip__check'
    icon.setAttribute('aria-hidden', 'true')
    const text = document.createElement('span')
    text.textContent = label
    chip.append(input, icon, text)
    return chip
  }

  linksOf(row) {
    try {
      return JSON.parse(row.dataset.onlyWith || '[]')
    } catch {
      return []
    }
  }

  // A removed saved option stays in the form, hidden, so its name is still there.
  nameOfRef(ref) {
    const row = this.rowTargets.find((target) => target.dataset.ref === ref)
    return (row && this.nameInput(row).value.trim()) || 'An option'
  }

  choiceRows() {
    return this.sectionTargets.filter((section) => section.dataset.kind === 'choice').flatMap((section) => this.rowsIn(section))
  }

  addGroup(name) {
    const section = this.groupTemplateTarget.content.firstElementChild.cloneNode(true)
    this.groupNameInput(section).value = name
    const addOns = this.sectionTargets.find((target) => target.dataset.kind === 'add_on')
    this.sectionsTarget.insertBefore(section, addOns)
    return section
  }

  discard(row) {
    if (row.dataset.newRecord === 'true') {
      row.remove()
      return
    }
    this.field(row, 'destroy').value = '1'
    row.hidden = true
  }

  // Each row takes its section's kind; a group keeps one default, and Included has none. The chips show while no row
  // does.
  restamp() {
    if (this.hasStarterTarget) this.starterTarget.hidden = this.rowTargets.some((row) => !row.hidden)
    this.sectionTargets.forEach((section) => {
      const kind = section.dataset.kind
      const group = kind === 'choice' ? this.groupNameInput(section).value.trim() : ''
      let picked = false
      this.rowsIn(section).forEach((row) => {
        this.field(row, 'choice_group').value = group
        this.field(row, 'optional').value = kind === 'add_on' ? '1' : '0'
        const box = this.pickInput(row)
        if (kind === 'included' || (kind === 'choice' && picked && !row.hidden)) box.checked = false
        if (kind === 'choice' && box.checked && !row.hidden) picked = true
      })
    })
    this.renderLinks()
  }

  number() {
    this.restamp()
    this.rowTargets.forEach((row, index) => { this.field(row, 'position').value = index })
  }

  // Two groups with one name would merge on save, so the second is flagged before the form can submit.
  checkGroupNames() {
    const seen = new Set()
    this.sectionTargets.filter((section) => section.dataset.kind === 'choice').forEach((section) => {
      const input = this.groupNameInput(section)
      const key = input.value.trim().toLowerCase()
      input.setCustomValidity(key && seen.has(key) ? 'Another group already has this name.' : '')
      if (key) seen.add(key)
    })
  }

  listIn(section) {
    return this.listTargets.find((list) => section.contains(list))
  }

  rowsIn(section) {
    return this.rowTargets.filter((row) => section.contains(row))
  }

  field(row, name) {
    return row.querySelector(`[data-field="${name}"]`)
  }

  nameInput(row) {
    return row.querySelector('[name$="[name]"]')
  }

  pickInput(row) {
    return row.querySelector('input[type="checkbox"][name$="[selected]"]')
  }

  groupNameInput(section) {
    return section.querySelector('.pb-section__name')
  }
}
